
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { QueueItem, WriterConfig, WordPressConfig, COSTS, SavedCampaign } from '../types';
import { db } from '../services/firebase';
import { saveCampaign } from '../services/storageService';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

interface QueueContextType {
  queue: QueueItem[];
  isProcessing: boolean;
  progress: number;
  waitTimer: number;
  activeCampaignId: string | null;
  startServerCampaign: (topics: string[], writerConfig: WriterConfig, wpConfig: WordPressConfig, options: any) => Promise<void>;
  stopQueue: () => void;
  clearQueue: () => void;
  // Removed local startQueue as it is replaced by server-side logic
  startQueue: (topics: string[], writerConfig: WriterConfig, wpConfig: WordPressConfig, options: any) => void;
}

const QueueContext = createContext<QueueContextType | undefined>(undefined);

export const QueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const toast = useToast();
  
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [waitTimer, setWaitTimer] = useState(0);
  const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null);

  // Listener for active campaign
  useEffect(() => {
      let unsub: any;
      if (activeCampaignId && user) {
          setIsProcessing(true);
          const campRef = db.collection('auto_writer_campaigns').doc(activeCampaignId);
          unsub = campRef.onSnapshot((doc) => {
              if (doc.exists) {
                  const data = doc.data();
                  if (data?.items) {
                      setQueue(data.items);
                      
                      // Calculate progress
                      const total = data.items.length;
                      const completed = data.items.filter((i: any) => i.status === 'success' || i.status === 'error').length;
                      setProgress(Math.round((completed / total) * 100));

                      if (data.status === 'completed' || completed === total) {
                          setIsProcessing(false);
                          toast.success("Chiến dịch đã hoàn tất!");
                          setActiveCampaignId(null);
                      }
                  }
              }
          });
      }
      return () => { if (unsub) unsub(); };
  }, [activeCampaignId, user]);

  const startServerCampaign = async (topics: string[], writerConfig: WriterConfig, wpConfig: WordPressConfig, options: any) => {
      if (!user) return;

      // 1. Prepare items
      const items: QueueItem[] = topics.map((topic, index) => {
        const startDate = new Date(options.scheduleStart);
        const publishDate = new Date(startDate.getTime() + (index * (24 / options.postsPerDay) * 60 * 60 * 1000));
        return { 
            id: `job_${Date.now()}_${index}`, 
            topic: topic.trim(), 
            status: 'pending', 
            scheduledFor: publishDate.toISOString(), 
            logs: ["Đang chờ Server..."] 
        };
      });

      // 2. Create Campaign Doc directly in Firestore
      // Now SavedCampaign interface includes 'items', so no need for 'as any' or unsafe casting if inputs are correct.
      const campaignPayload: Omit<SavedCampaign, 'id' | 'userId' | 'createdAt' | 'updatedAt'> = {
          name: `Server Batch ${new Date().toLocaleDateString('vi-VN')} - ${topics.length} posts`,
          topics,
          writerConfig,
          wpConfig,
          scheduleStart: options.scheduleStart,
          postsPerDay: options.postsPerDay,
          status: 'running',
          items: items // Save initial items state
      };

      const campaignId = await saveCampaign(user.uid, campaignPayload);

      setActiveCampaignId(campaignId);
      setQueue(items);

      // 3. Trigger Server Worker
      try {
          const res = await fetch('/api/auto-writer/start', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  campaignId,
                  items,
                  writerConfig,
                  wpConfig,
                  options
              })
          });
          
          if (!res.ok) throw new Error('Failed to start server worker');
      } catch (e) {
          console.error("Server start error", e);
          toast.error("Lỗi kích hoạt Server Worker, nhưng dữ liệu đã lưu.");
      }
  };

  const stopQueue = () => {
      // For server side, we can't easily "pause" mid-flight without complex logic
      // Just stop listening for now
      setActiveCampaignId(null);
      setIsProcessing(false);
      toast.info("Đã ngắt kết nối theo dõi (Server vẫn có thể đang chạy).");
  };

  const clearQueue = () => {
      setQueue([]);
      setProgress(0);
      setActiveCampaignId(null);
      setIsProcessing(false);
  };

  const startQueue = () => {
      console.warn("Legacy startQueue called. Use startServerCampaign instead.");
  };

  return (
    <QueueContext.Provider value={{ queue, isProcessing, progress, waitTimer, activeCampaignId, startServerCampaign, stopQueue, clearQueue, startQueue }}>
      {children}
    </QueueContext.Provider>
  );
};

export const useQueue = () => {
  const context = useContext(QueueContext);
  if (!context) throw new Error("useQueue must be used within QueueProvider");
  return context;
};
